import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { t } from '../../../utils/translations';
import { formatTime } from '../../../utils/dateUtils';

const FormActions = ({ 
  onSave, 
  onPreview, 
  onPublish,
  onCancel,
  isLoading = false,
  hasUnsavedChanges = false,
  formData = {},
  disabled = false 
}) => {
  const [lastSaved, setLastSaved] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error

  const handleSave = async (action = 'save') => {
    setSaveStatus('saving');
    
    try {
      await onSave(action);
      setLastSaved(new Date());
      setSaveStatus('saved');
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview(formData);
    }
  };

  const handlePublish = async () => {
    await handleSave('publish');
    if (onPublish) {
      onPublish(formData);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        t('officeForm.unsavedChangesConfirm')
      );
      if (!confirmed) return;
    }
    
    if (onCancel) {
      onCancel();
    } else {
      window.history?.back();
    }
  };

  // Validation status - COMMENTED OUT: No longer using validation
  // const getValidationStatus = () => {
  //   const requiredFields = [
  //     'name_ar', 'name_en', 'title_ar', 'title_en', 
  //     'biography_ar', 'contacts'
  //   ];
  //   
  //   const missingFields = requiredFields?.filter(field => {
  //     if (field === 'contacts') {
  //       return !formData?.contacts || formData?.contacts?.length === 0 || 
  //              !formData?.contacts?.some(c => c?.value?.trim());
  //     }
  //     return !formData?.[field] || formData?.[field]?.trim() === '';
  //   });

  //   return {
  //     isValid: missingFields?.length === 0,
  //     missingCount: missingFields?.length,
  //     totalRequired: requiredFields?.length
  //   };
  // };

  // const validation = getValidationStatus();

  return (
    <div className="space-y-6">
      {/* Auto-save Status */}
      {lastSaved && (
        <div className="flex items-center justify-between p-3 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon 
              name={saveStatus === 'saving' ? 'Loader2' : 'CheckCircle'} 
              size={16} 
              className={`text-success ${saveStatus === 'saving' ? 'animate-spin' : ''}`}
            />
            <span className="text-sm text-success">
              {saveStatus === 'saving' ? t('officeFormComponents.saving') : t('officeFormComponents.changesSaved')}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {t('officeFormComponents.lastSaved')}: {formatTime(lastSaved)}
          </span>
        </div>
      )}
      {/* Validation Status - COMMENTED OUT: No longer showing validation status */}
      {/* <div className={`p-4 rounded-lg border ${
        validation?.isValid 
          ? 'bg-success/5 border-success/20' :'bg-warning/5 border-warning/20'
      }`}>
        <div className="flex items-start space-x-3">
          <Icon 
            name={validation?.isValid ? 'CheckCircle' : 'AlertTriangle'} 
            size={20} 
            className={validation?.isValid ? 'text-success' : 'text-warning'}
          />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground">
              {t('officeFormComponents.formValidationStatus')}
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              {validation?.isValid 
                ? t('officeFormComponents.allRequiredFieldsCompleted')
                : t('officeFormComponents.requiredFieldsNeedAttention', { missing: validation?.missingCount, total: validation?.totalRequired })
              }
            </p>
            
            {!validation?.isValid && (
              <div className="mt-2">
                <p className="text-xs text-warning">
                  {t('officeFormComponents.completeAllRequiredFields')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div> */}
      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && saveStatus !== 'saving' && (
        <div className="flex items-center space-x-2 p-3 bg-warning/5 border border-warning/20 rounded-lg">
          <Icon name="AlertCircle" size={16} className="text-warning" />
          <span className="text-sm text-warning">
            {t('articleEditor.unsavedChanges')}
          </span>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Primary Actions */}
        <div className="flex flex-1 space-x-3">
          <Button
            variant="outline"
            onClick={() => handleSave('draft')}
            disabled={disabled || saveStatus === 'saving'}
            loading={saveStatus === 'saving'}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
            className="flex-1"
          >
            {t('articleEditor.saveDraft')}
          </Button>

          <Button
            variant="secondary"
            onClick={handlePreview}
            disabled={disabled}
            iconName="Eye"
            iconPosition="left"
            iconSize={16}
            className="flex-1"
          >
            {t('articleEditor.preview')}
          </Button>
        </div>

        {/* Publish Button */}
        <Button
          variant="default"
          onClick={handlePublish}
          disabled={disabled || saveStatus === 'saving'}
          loading={saveStatus === 'saving'}
          iconName="Send"
          iconPosition="left"
          iconSize={16}
          className="sm:w-auto"
        >
          {t('officeFormComponents.publishPosition')}
        </Button>
      </div>
      {/* Secondary Actions */}
      <div className="flex justify-between items-center pt-4 border-t border-border">
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={disabled || saveStatus === 'saving'}
          iconName="ArrowLeft"
          iconPosition="left"
          iconSize={16}
        >
          {t('officeFormComponents.backToOffices')}
        </Button>

        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.href = '/offices-management'}
            disabled={disabled}
            iconName="List"
            iconSize={14}
          >
            {t('officeFormComponents.viewAllOffices')}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.href = '/dashboard'}
            disabled={disabled}
            iconName="Home"
            iconSize={14}
          >
            {t('officeFormComponents.dashboard')}
          </Button>
        </div>
      </div>
      {/* Keyboard Shortcuts Info */}
      <div className="p-3 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Keyboard" size={16} className="text-muted-foreground mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">{t('officeFormComponents.keyboardShortcuts')}:</p>
            <div className="grid grid-cols-2 gap-2">
              <span>{t('officeFormComponents.ctrlS')}</span>
              <span>{t('officeFormComponents.ctrlP')}</span>
              <span>{t('officeFormComponents.ctrlEnter')}</span>
              <span>{t('officeFormComponents.esc')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormActions;